/*
 * AdminLogin — dedicated admin sign-in page.
 *
 * Two paths:
 *   • Password + TOTP (default)
 *   • Magic link (email-based, passwordless)
 *   • Recovery code (break-glass — admin lost TOTP device)
 *
 * Lives at /admin/login. Separate from /login so phishing kits that
 * target the generic login form don't auto-grab admin creds.
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Shield, Mail, KeyRound, Fingerprint, ArrowRight, Loader2,
  Wand2, LifeBuoy,
} from "lucide-react";


function persistSession(setUser, data) {
  if (data?.token) localStorage.setItem("token", data.token);
  setUser?.(data);
}


const MODES = [
  { id: "password",   label: "Password + 2FA",   icon: KeyRound },
  { id: "magic",      label: "Magic link",        icon: Wand2 },
  { id: "recovery",   label: "Recovery code",     icon: LifeBuoy },
];


export default function AdminLogin() {
  const [mode, setMode] = useState("password");
  const [searchParams] = useSearchParams();
  const magicToken = searchParams.get("magic") || searchParams.get("token");
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  // If a magic token is in the URL, redeem it immediately
  useEffect(() => {
    if (!magicToken) return;
    (async () => {
      setBusy(true);
      try {
        const r = await api.post("/admin/auth/magic-link/redeem", { token: magicToken });
        persistSession(setUser, r.data);
        toast.success("Signed in via magic link · session expires in 12 hours");
        navigate("/admin");
      } catch (e) {
        toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Magic link invalid or expired");
      } finally { setBusy(false); }
    })();
    // eslint-disable-next-line
  }, [magicToken]);

  if (magicToken && busy) {
    return (
      <Splash>
        <Loader2 className="w-6 h-6 animate-spin mb-2"/>
        <div className="text-sm">Verifying magic link…</div>
      </Splash>
    );
  }

  return (
    <Splash>
      <div className="w-[min(100%,420px)] space-y-5">
        <header className="space-y-1">
          <div className="inline-flex items-center gap-1.5 chip bg-foreground/5 border-border text-[10.5px] mono">
            <Shield className="w-3 h-3"/>RESTRICTED · ADMIN PORTAL
          </div>
          <h1 className="display text-3xl tracking-tight">Sign in to admin</h1>
          <p className="text-[12.5px] text-foreground/55">
            Internal platform-staff access only. All sign-in attempts are logged with IP and device.
          </p>
        </header>

        {/* Mode picker */}
        <div className="flex gap-1 border-b">
          {MODES.map(m => {
            const Icon = m.icon;
            return (
              <button key={m.id} onClick={() => setMode(m.id)}
                data-testid={`admin-login-mode-${m.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px] border-b-2 transition-colors ${
                  mode === m.id ? "border-foreground text-foreground" : "border-transparent text-foreground/55 hover:text-foreground/80"
                }`}>
                <Icon className="w-3.5 h-3.5"/>{m.label}
              </button>
            );
          })}
        </div>

        {mode === "password" && <PasswordForm setUser={setUser} navigate={navigate}/>}
        {mode === "magic"    && <MagicForm/>}
        {mode === "recovery" && <RecoveryForm setUser={setUser} navigate={navigate}/>}

        <SsoBlock />

        <div className="pt-3 text-[11px] text-foreground/45">
          Not an admin? <a href="/login" className="underline">Use the regular sign-in</a>.
        </div>
      </div>
    </Splash>
  );
}


function PasswordForm({ setUser, navigate }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [busy, setBusy] = useState(false);
  // Plan SX dual-MFA — admin: password → TOTP → SMS step
  const [smsStep, setSmsStep] = useState(null); // {challenge_token, masked}
  const [smsCode, setSmsCode] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      // Phase 2 · admin uses /sms/login/challenge so TOTP + SMS are dual-validated.
      const r = await api.post("/sms/login/challenge", {
        email, password, portal: "admin",
        ...(totp ? { totp_code: totp } : {}),
      });
      const d = r.data;
      if (d.requires_sms_mfa) {
        setSmsStep({ challenge_token: d.challenge_token, masked: d.masked });
        toast.success(`Code sent to ${d.masked}. Enter it below.`);
        return;
      }
      // No SMS configured for this account → fall back to /auth/login
      const r2 = await api.post("/auth/login", {
        email, password, portal: "admin",
        ...(totp ? { totp_code: totp } : {}),
      });
      handleLoginSuccess(r2.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail?.code;
      if (code === "admin_totp_required") {
        toast.error("Enter your authenticator code.");
      } else if (code === "admin_2fa_required") {
        toast.error("Admin 2FA setup required.");
      } else if (code === "admin_sms_phone_required") {
        toast.error("Admin SMS phone enrolment required. Contact platform owner.");
      } else if (code === "wrong_portal_use_admin_login") {
        toast.error(detail.message);
      } else if (code === "ip_not_allowed") {
        toast.error(`IP ${detail.your_ip} is not on the admin allowlist.`);
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error(detail?.message || "Sign-in failed");
      }
    } finally { setBusy(false); }
  };

  const verifySms = async (e) => {
    e?.preventDefault();
    if (!smsCode || smsCode.length < 4) return;
    setBusy(true);
    try {
      const r = await api.post("/sms/login/verify", {
        challenge_token: smsStep.challenge_token, code: smsCode,
      });
      handleLoginSuccess(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid SMS code");
    } finally { setBusy(false); }
  };

  function handleLoginSuccess(d) {
    if (d.requires_2fa_setup) {
      persistSession(setUser, d);
      navigate("/admin/settings?tab=security");
      toast.warning("Enrol TOTP to complete admin security setup");
      return;
    }
    persistSession(setUser, d);
    toast.success("Signed in · welcome back.");
    navigate("/admin");
  }

  if (smsStep) {
    return (
      <form onSubmit={verifySms} className="space-y-3" data-testid="admin-login-sms-form">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900">
          <Shield className="w-3 h-3 inline mr-1"/>
          Code sent to <strong>{smsStep.masked}</strong>. Step 2 of 2.
        </div>
        <div>
          <Label htmlFor="admin-sms">SMS code (6 digits)</Label>
          <Input id="admin-sms" type="text" inputMode="numeric" maxLength={6}
            value={smsCode} onChange={(e) => setSmsCode(e.target.value)}
            placeholder="123456" autoFocus autoComplete="one-time-code"
            data-testid="admin-login-sms-code"/>
        </div>
        <Button type="submit" className="w-full" disabled={busy || smsCode.length < 4} data-testid="admin-login-sms-submit">
          {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/>}
          Verify <ArrowRight className="w-3.5 h-3.5 ml-1"/>
        </Button>
        <button type="button" onClick={() => { setSmsStep(null); setSmsCode(""); }}
                className="text-[11px] text-foreground/55 hover:text-foreground w-full text-center">
          ← Back to login
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="admin-login-password-form">
      <div>
        <Label htmlFor="admin-email">Work email</Label>
        <Input id="admin-email" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)} required
          autoComplete="email" data-testid="admin-login-email"/>
      </div>
      <div>
        <Label htmlFor="admin-pw">Password</Label>
        <Input id="admin-pw" type="password" value={password}
          onChange={(e) => setPassword(e.target.value)} required
          autoComplete="current-password" data-testid="admin-login-password"/>
      </div>
      <div>
        <Label htmlFor="admin-totp">Authenticator code (TOTP)</Label>
        <Input id="admin-totp" type="text" inputMode="numeric"
          maxLength={6} placeholder="123456"
          value={totp} onChange={(e) => setTotp(e.target.value)}
          autoComplete="one-time-code" data-testid="admin-login-totp"/>
      </div>
      <div className="rounded-lg bg-foreground/3 border px-2.5 py-1.5 text-[10.5px] text-foreground/65 flex items-center gap-1.5">
        <Shield className="w-3 h-3"/>
        Dual-MFA: password + TOTP + SMS to your verified phone
      </div>
      <Button type="submit" className="w-full" disabled={busy} data-testid="admin-login-submit">
        {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/>}
        Continue <ArrowRight className="w-3.5 h-3.5 ml-1"/>
      </Button>
    </form>
  );
}


function MagicForm() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/auth/magic-link/request", { email });
      setSent(true);
      toast.success("If that email is an admin, a sign-in link was sent.");
    } catch (e) {
      // Always succeed on UI to avoid email enumeration
      setSent(true);
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="rounded-xl border bg-emerald-50 border-emerald-200 p-4 text-emerald-900 text-[12.5px]" data-testid="magic-sent">
        <Mail className="w-4 h-4 mb-1.5"/>
        <div className="font-medium">Check your inbox.</div>
        <div className="text-[11.5px] mt-1">If <span className="mono">{email}</span> is an admin, a one-time sign-in link is on its way. The link expires in 15 minutes and can only be used once.</div>
        <Button variant="ghost" size="sm" className="mt-2" onClick={() => { setSent(false); setEmail(""); }}>Send another</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="admin-login-magic-form">
      <div>
        <Label htmlFor="admin-magic-email">Work email</Label>
        <Input id="admin-magic-email" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)} required
          data-testid="magic-email"/>
      </div>
      <p className="text-[11px] text-foreground/55">
        We'll email a single-use sign-in link valid for 15 minutes. No password required.
      </p>
      <Button type="submit" className="w-full" disabled={busy} data-testid="magic-submit">
        {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/>}
        <Wand2 className="w-3.5 h-3.5 mr-1"/>Send magic link
      </Button>
    </form>
  );
}


function RecoveryForm({ setUser, navigate }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/auth/recovery-code/redeem", {
        email, password, code,
      });
      persistSession(setUser, r.data);
      toast.success(r.data.warning || "Signed in via recovery code");
      navigate("/admin/settings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid credentials or code");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="admin-login-recovery-form">
      <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900">
        Use this only if you've lost access to your authenticator app. Codes are one-time-use.
      </div>
      <div>
        <Label htmlFor="rec-email">Work email</Label>
        <Input id="rec-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="recovery-email"/>
      </div>
      <div>
        <Label htmlFor="rec-pw">Password</Label>
        <Input id="rec-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="recovery-pw"/>
      </div>
      <div>
        <Label htmlFor="rec-code">Recovery code (XXXX-XXXX)</Label>
        <Input id="rec-code" className="mono" value={code} onChange={(e) => setCode(e.target.value)} required data-testid="recovery-code"/>
      </div>
      <Button type="submit" className="w-full" disabled={busy} data-testid="recovery-submit">
        {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/>}
        <Fingerprint className="w-3.5 h-3.5 mr-1"/>Sign in with recovery code
      </Button>
    </form>
  );
}


function SsoBlock() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/auth/sso/config");
        setCfg(r.data);
      } catch (e) { /* SSO unavailable — quietly hide */ }
    })();
  }, []);

  if (!cfg?.enabled) return null;

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/sso/callback";
    window.location.href = `${cfg.auth_url}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="pt-4 border-t mt-4 space-y-2" data-testid="admin-sso-block">
      <div className="text-[11px] text-foreground/50 uppercase tracking-wider">Or sign in with</div>
      <button
        type="button"
        onClick={handleGoogle}
        data-testid="admin-sso-google-btn"
        className="w-full flex items-center justify-center gap-2 px-4 py-2 border rounded-md text-[13px] font-medium bg-white hover:bg-foreground/5 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        Sign in with Google
      </button>
      <div className="text-[10.5px] text-foreground/45">
        Approved domains: {(cfg.allowed_domains || []).join(", ")}
      </div>
    </div>
  );
}




function Splash({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-background to-foreground/5">
      <div className="flex flex-col items-center">{children}</div>
    </div>
  );
}
